<template>
    <div class="table-question-settings" ref="root">
        <div class="tqs-toolbar">
            <div class="tqs-segmented" role="group" :aria-label="t('forms-table-rows_mode')">
                <button
                    type="button"
                    :class="{ active: s.rows.mode === 'dynamic' }"
                    @click="changeMode('dynamic')"
                >
                    {{ t("forms-table-mode_dynamic") }}
                </button>
                <button
                    type="button"
                    :class="{ active: s.rows.mode === 'fixed' }"
                    @click="changeMode('fixed')"
                >
                    {{ t("forms-table-mode_fixed") }}
                </button>
            </div>

            <div class="tqs-toolbar-right">
                <kv-button
                    v-if="view === 'edit'"
                    variant="text"
                    size="small"
                    prepend-icon="ui-copy-03"
                    @click="importOpen = !importOpen"
                >
                    {{ t("forms-table-paste_button") }}
                </kv-button>
                <div class="tqs-segmented" role="group">
                    <button type="button" :class="{ active: view === 'edit' }" @click="view = 'edit'">
                        {{ t("forms-table-view_edit") }}
                    </button>
                    <button type="button" :class="{ active: view === 'test' }" @click="openTest">
                        {{ t("forms-table-view_test") }}
                    </button>
                </div>
            </div>
        </div>

        <p class="tqs-hint">
            {{ s.rows.mode === "dynamic" ? t("forms-table-mode_dynamic_hint") : t("forms-table-mode_fixed_hint") }}
        </p>

        <div class="tqs-import" v-if="importOpen && view === 'edit'">
            <textarea
                v-model="importText"
                class="tqs-import-text"
                rows="4"
                :placeholder="t('forms-table-paste_placeholder')"
            ></textarea>
            <div class="tqs-import-actions">
                <span class="tqs-hint">{{ t("forms-table-paste_hint") }}</span>
                <kv-button variant="outlined" size="small" @click="importOpen = false">
                    {{ t("forms-btn_cancel") }}
                </kv-button>
                <kv-button variant="filled" size="small" :disabled="!importText.trim()" @click="runImport">
                    {{ t("forms-table-paste_apply") }}
                </kv-button>
            </div>
        </div>

        <template v-if="view === 'edit'">
            <div class="tqs-formula-bar">
                <span class="tqs-ref">{{ selected ? selectedRef : "" }}</span>
                <span class="tqs-fx">ƒx</span>
                <span class="tqs-formula-text" :class="{ muted: !selectedText }">
                    {{ selectedText || t("forms-table-formula_bar_hint") }}
                </span>
                <span class="tqs-formula-result" v-if="selectedResult !== null">= {{ selectedResult }}</span>
                <button
                    v-if="selected && selected.kind === 'data'"
                    type="button"
                    class="tqs-lock"
                    :class="{ active: selectedLocked }"
                    :disabled="selectedIsFormula || isFormulaText"
                    :title="t('forms-table-lock_hint')"
                    @mousedown.prevent
                    @click="toggleLock"
                >
                    <Icon icon-class="ui-lock-01" size="2" />
                    {{ selectedLocked ? t("forms-table-locked") : t("forms-table-lock") }}
                </button>
            </div>
            <p class="tqs-cell-error" v-if="inputError">{{ inputError }}</p>

            <div class="tqs-grid-wrap" @scroll="closeMenu">
                <table class="tqs-grid">
                    <thead>
                        <tr class="tqs-letters">
                            <th class="tqs-rownum"></th>
                            <th class="tqs-label-col"></th>
                            <th
                                v-for="(col, j) in s.columns"
                                :key="`l-${j}`"
                                :class="{
                                    'is-drop': dragOver === j,
                                    'has-error': !!problemsByWhere[letter(j)],
                                }"
                                draggable="true"
                                :title="problemsByWhere[letter(j)] || t('forms-table-drag_hint')"
                                @dragstart="onDragStart($event, j)"
                                @dragover.prevent="dragOver = j"
                                @dragleave="dragOver = null"
                                @drop.prevent="onDrop(j)"
                                @dragend="dragOver = null"
                                @mousedown="onLetterMouseDown($event, j)"
                            >
                                <span class="tqs-letter">{{ letter(j) }}</span>
                                <span class="tqs-type-tag">{{ typeShort(col.type) }}</span>
                                <button
                                    type="button"
                                    class="tqs-col-menu-btn"
                                    :aria-label="t('forms-table-column_menu')"
                                    @mousedown.stop
                                    @click.stop="openMenu($event, j)"
                                >
                                    ⋯
                                </button>
                            </th>
                            <th class="tqs-add-col">
                                <button
                                    type="button"
                                    :disabled="s.columns.length >= maxColumns"
                                    :title="t('forms-table-add_column')"
                                    :aria-label="t('forms-table-add_column')"
                                    @click="addColumn"
                                >
                                    <Icon icon-class="ui-plus" size="2" />
                                </button>
                            </th>
                        </tr>
                        <tr class="tqs-headers">
                            <th class="tqs-rownum"></th>
                            <th class="tqs-label-col tqs-muted">
                                {{ s.rows.mode === "fixed" || s.rows.summary.length ? t("forms-table-row_label_header") : "" }}
                            </th>
                            <th
                                v-for="(col, j) in s.columns"
                                :key="`hl-${j}`"
                                :style="{ minWidth: editorWidth(col) + 'px' }"
                            >
                                <input
                                    class="tqs-header-input"
                                    v-model="col.label"
                                    :placeholder="t('forms-table-column_title_placeholder')"
                                    @focus="stopEditing"
                                    @paste="onHeaderPaste($event, j)"
                                />
                                <span v-if="col.required" class="tqs-required">*</span>
                            </th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(row, i) in dataRows" :key="`d-${i}`">
                            <th class="tqs-rownum">
                                <span>{{ i + 1 }}</span>
                                <span class="tqs-row-actions" v-if="s.rows.mode === 'fixed'">
                                    <button
                                        type="button"
                                        :disabled="i === 0"
                                        :aria-label="t('forms-table-move_up')"
                                        @click="moveRow(i, i - 1)"
                                    >▲</button>
                                    <button
                                        type="button"
                                        :disabled="i === dataRows.length - 1"
                                        :aria-label="t('forms-table-move_down')"
                                        @click="moveRow(i, i + 1)"
                                    >▼</button>
                                    <button
                                        type="button"
                                        :disabled="dataRows.length <= 1"
                                        :aria-label="t('forms-table-remove_row')"
                                        @click="removeRow(i)"
                                    >✕</button>
                                </span>
                            </th>
                            <th class="tqs-label-col">
                                <input
                                    v-if="s.rows.mode === 'fixed'"
                                    class="tqs-row-label-input"
                                    v-model="row.label"
                                    :placeholder="t('forms-table-row_label_placeholder')"
                                    @focus="stopEditing"
                                    @paste="onRowLabelPaste($event, i)"
                                />
                                <span v-else class="tqs-each-row">{{ t("forms-table-each_row") }}</span>
                            </th>
                            <td
                                v-for="(col, j) in s.columns"
                                :key="`d-${i}-${j}`"
                                :class="cellClasses({ kind: 'data', row: i, col: j })"
                                :title="cellTitle({ kind: 'data', row: i, col: j })"
                                @mousedown="onCellMouseDown($event, { kind: 'data', row: i, col: j })"
                                @click="onCellClick({ kind: 'data', row: i, col: j })"
                            >
                                <input
                                    v-if="isEditing({ kind: 'data', row: i, col: j })"
                                    ref="cellInput"
                                    class="tqs-cell-input"
                                    :class="{ 'is-formula-input': isFormulaText }"
                                    v-model="editText"
                                    @keydown="onEditKeydown"
                                    @blur="onEditBlur"
                                    @paste="onCellPaste"
                                    @click="lastRefRange = null"
                                />
                                <template v-else>
                                    <span class="tqs-cell-text">{{ cellDisplay({ kind: 'data', row: i, col: j }) }}</span>
                                    <Icon
                                        v-if="isLocked({ kind: 'data', row: i, col: j })"
                                        class="tqs-cell-lock"
                                        icon-class="ui-lock-01"
                                        size="1"
                                    />
                                </template>
                            </td>
                            <td></td>
                        </tr>
                        <tr v-if="s.rows.mode === 'fixed'" class="tqs-add-row">
                            <td :colspan="s.columns.length + 3">
                                <button type="button" :disabled="dataRows.length >= maxFixedRows" @click="addRow">
                                    <Icon icon-class="ui-plus" size="2" /> {{ t("forms-table-add_row") }}
                                </button>
                            </td>
                        </tr>
                        <tr v-for="(row, k) in s.rows.summary" :key="`s-${k}`" class="tqs-summary">
                            <th class="tqs-rownum">
                                <span>{{ dataRows.length + k + 1 }}</span>
                                <span class="tqs-row-actions">
                                    <button
                                        type="button"
                                        :aria-label="t('forms-table-remove_summary')"
                                        @click="removeSummary(k)"
                                    >✕</button>
                                </span>
                            </th>
                            <th class="tqs-label-col">
                                <input
                                    class="tqs-row-label-input"
                                    v-model="row.label"
                                    :placeholder="t('forms-table-summary_label_placeholder')"
                                    @focus="stopEditing"
                                />
                            </th>
                            <td
                                v-for="(col, j) in s.columns"
                                :key="`s-${k}-${j}`"
                                :class="cellClasses({ kind: 'summary', row: k, col: j })"
                                :title="cellTitle({ kind: 'summary', row: k, col: j })"
                                @mousedown="onCellMouseDown($event, { kind: 'summary', row: k, col: j })"
                                @click="onCellClick({ kind: 'summary', row: k, col: j })"
                            >
                                <input
                                    v-if="isEditing({ kind: 'summary', row: k, col: j })"
                                    ref="cellInput"
                                    class="tqs-cell-input"
                                    :class="{ 'is-formula-input': isFormulaText }"
                                    v-model="editText"
                                    @keydown="onEditKeydown"
                                    @blur="onEditBlur"
                                    @click="lastRefRange = null"
                                />
                                <span v-else class="tqs-cell-text">
                                    {{ cellDisplay({ kind: 'summary', row: k, col: j }) }}
                                </span>
                            </td>
                            <td></td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div class="tqs-below">
                <kv-button
                    variant="outlined"
                    size="small"
                    prepend-icon="ui-plus"
                    :disabled="s.rows.summary.length >= maxSummary"
                    @click="addSummary"
                >
                    {{ t("forms-table-add_summary") }}
                </kv-button>
                <span class="tqs-hint">{{ t("forms-table-summary_hint") }}</span>
            </div>

            <div class="tqs-fields" v-if="s.rows.mode === 'dynamic'">
                <kv-input
                    type="number"
                    variant="outlined"
                    :min="0"
                    :max="maxDynamicRows"
                    :label="t('forms-table-rows_min')"
                    v-model="s.rows.min"
                />
                <kv-input
                    type="number"
                    variant="outlined"
                    :min="1"
                    :max="maxDynamicRows"
                    :label="t('forms-table-rows_max')"
                    v-model="s.rows.max"
                />
                <kv-input
                    variant="outlined"
                    :label="t('forms-table-add_label')"
                    :placeholder="t('forms-table-add_row_default')"
                    v-model="s.rows.addLabel"
                />
            </div>
            <p class="tqs-hint" v-if="s.rows.mode === 'dynamic'">{{ t("forms-table-rows_min_hint") }}</p>

            <div class="tqs-value-number">
                <label class="tqs-field-label" for="tqs-value-number">{{ t("forms-table-value_number") }}</label>
                <select id="tqs-value-number" class="tqs-select" v-model="valueNumberModel">
                    <option value="">{{ t("forms-table-value_number_default") }}</option>
                    <option v-for="opt in valueNumberChoices" :key="opt.value" :value="opt.value">
                        {{ opt.label }}
                    </option>
                </select>
                <p class="tqs-hint">{{ t("forms-table-value_number_hint") }}</p>
            </div>
        </template>

        <template v-else>
            <div class="tqs-test-bar">
                <span class="tqs-hint">{{ t("forms-table-test_hint") }}</span>
                <div class="tqs-segmented" role="group">
                    <button type="button" :class="{ active: !previewMobile }" @click="previewMobile = false">
                        {{ t("forms-table-preview_desktop") }}
                    </button>
                    <button type="button" :class="{ active: previewMobile }" @click="previewMobile = true">
                        {{ t("forms-table-preview_mobile") }}
                    </button>
                </div>
            </div>
            <table-answer-input
                :settings="previewSettings"
                v-model="previewValues"
                :mobile="previewMobile"
            />
        </template>

        <ul class="tqs-problems" v-if="showProblems && problems.length">
            <li v-for="(problem, index) in problems" :key="index">{{ problem.message }}</li>
        </ul>

        <div
            v-if="menu"
            ref="menu"
            class="tqs-menu"
            :style="{ top: menu.top + 'px', left: menu.left + 'px' }"
            @mousedown.stop
        >
            <div class="tqs-menu-title">
                {{ t("forms-table-column") }} {{ letter(menu.col) }}
            </div>
            <label class="tqs-field-label">{{ t("forms-table-column_type") }}</label>
            <select class="tqs-select" :value="menuColumn.type" @change="changeType($event.target.value)">
                <option v-for="type in columnTypes" :key="type" :value="type">
                    {{ t("forms-table-type_" + type) }}
                </option>
            </select>
            <label class="tqs-check">
                <input type="checkbox" v-model="menuColumn.required" />
                {{ t("forms-table-required") }}
            </label>
            <div class="tqs-menu-grid">
                <label>
                    <span class="tqs-field-label">{{ t("forms-table-alias") }}</span>
                    <input class="tqs-text" v-model="menuColumn.alias" :placeholder="t('forms-table-alias_placeholder')" />
                </label>
                <label>
                    <span class="tqs-field-label">{{ t("forms-table-min_width") }}</span>
                    <input
                        class="tqs-text"
                        type="number"
                        min="40"
                        v-model.number="menuColumn.minWidth"
                        :placeholder="String(defaultWidth(menuColumn))"
                    />
                </label>
            </div>
            <p class="tqs-hint">{{ t("forms-table-alias_hint") }}</p>

            <div class="tqs-menu-grid" v-if="menuColumn.type === 'number'">
                <label>
                    <span class="tqs-field-label">{{ t("forms-table-number_min") }}</span>
                    <input class="tqs-text" type="number" v-model="menuColumn.min" />
                </label>
                <label>
                    <span class="tqs-field-label">{{ t("forms-table-number_max") }}</span>
                    <input class="tqs-text" type="number" v-model="menuColumn.max" />
                </label>
                <label>
                    <span class="tqs-field-label">{{ t("forms-table-number_decimals") }}</span>
                    <input class="tqs-text" type="number" min="0" max="6" v-model="menuColumn.decimals" />
                </label>
                <label>
                    <span class="tqs-field-label">{{ t("forms-table-number_prefix") }}</span>
                    <!-- i18n-validator-ignore-next-line: unit sample -->
                    <input class="tqs-text" v-model="menuColumn.prefix" placeholder="R$" />
                </label>
                <label>
                    <span class="tqs-field-label">{{ t("forms-table-number_suffix") }}</span>
                    <!-- i18n-validator-ignore-next-line: unit sample -->
                    <input class="tqs-text" v-model="menuColumn.suffix" placeholder="kg" />
                </label>
            </div>

            <div class="tqs-options" v-if="menuColumn.type === 'select'">
                <span class="tqs-field-label">{{ t("forms-questions_form-options_label") }}</span>
                <div
                    class="tqs-option"
                    :class="{ 'is-removed': option.removed }"
                    v-for="(option, index) in menuColumn.options"
                    :key="`o-${index}`"
                >
                    <template v-if="!option.removed">
                        <input
                            class="tqs-text"
                            v-model="option.text"
                            :placeholder="t('forms-questions_form-placeholder_option', [index + 1])"
                            @keydown.enter.prevent="addOption(index)"
                        />
                        <button
                            type="button"
                            class="tqs-icon-btn"
                            :aria-label="t('forms-questions_form-remove_option')"
                            @click="removeOption(index)"
                        >
                            <Icon icon-class="ui-x-close" size="2" />
                        </button>
                    </template>
                    <template v-else>
                        <span class="tqs-removed-text">{{ option.text }} · {{ t("forms-questions_form-option_will_remove") }}</span>
                        <button
                            type="button"
                            class="tqs-icon-btn"
                            :aria-label="t('forms-questions_form-undo_remove_option')"
                            @click="option.removed = false"
                        >
                            <Icon icon-class="ui-refresh-ccw-01" size="2" />
                        </button>
                    </template>
                </div>
                <button type="button" class="tqs-link" @click="addOption()">
                    + {{ t("forms-questions_form-btn_add_option") }}
                </button>
            </div>

            <div class="tqs-menu-actions">
                <button type="button" :disabled="menu.col === 0" @click="moveCol(menu.col, menu.col - 1)">
                    ← {{ t("forms-table-move_left") }}
                </button>
                <button
                    type="button"
                    :disabled="menu.col >= s.columns.length - 1"
                    @click="moveCol(menu.col, menu.col + 1)"
                >
                    {{ t("forms-table-move_right") }} →
                </button>
                <button type="button" :disabled="s.columns.length >= maxColumns" @click="duplicateCol(menu.col)">
                    {{ t("forms-table-duplicate") }}
                </button>
                <button
                    type="button"
                    class="danger"
                    :disabled="s.columns.length <= 1"
                    @click="removeCol(menu.col)"
                >
                    {{ t("forms-table-remove_column") }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import Icon from "../icon/icon";
import KvButton from "../form/kv-button.vue";
import KvInput from "../inputs/kv-input.vue";
import TableAnswerInput from "./table-answer-input.vue";
import { hasFormula } from "../../helpers/tableFormula";
import {
    COLUMN_TYPES,
    MAX_COLUMNS,
    MAX_DYNAMIC_ROWS,
    MAX_FIXED_ROWS,
    MAX_SUMMARY_ROWS,
    addFixedRow,
    addSummaryRow,
    cellCfgOf,
    cellInputText,
    cellRefOf,
    columnLetter,
    computeSafe,
    defaultMinWidth,
    duplicateColumn,
    formatTableCell,
    importMatrix,
    insertColumn,
    looksLikeTable,
    moveColumn,
    moveFixedRow,
    newColumn,
    normalizeTableSettings,
    parseTsv,
    pasteMatrixAt,
    prepareTableSettings,
    referencedRefs,
    removeColumn,
    removeFixedRow,
    removeSummaryRow,
    sampleInputValues,
    setCellInput,
    setRowsMode,
    toggleCellLock,
    validateTable,
    valueNumberOptions,
} from "../../helpers/tableQuestion";

const sameTarget = (a, b) => !!(a && b && a.kind === b.kind && a.row === b.row && a.col === b.col);

/*
  Visual editor of a table question: the table itself is the editor. Header
  cells hold the column titles, data cells hold default values or formulas
  (=...), and every structural change rewrites the formulas like a
  spreadsheet does. v-model is the settings object of the contract.
*/
export default {
    name: "TableQuestionSettings",
    components: { Icon, KvButton, KvInput, TableAnswerInput },
    props: {
        value: { type: Object, default: null },
        // Turned on by the question editor after a submit attempt.
        showProblems: { type: Boolean, default: true },
    },
    data() {
        return {
            s: prepareTableSettings(this.value, this.defaultLabels()),
            view: "edit",
            selected: null,
            editing: null,
            editText: "",
            inputError: "",
            lastRefRange: null,
            skipNextClick: false,
            menu: null,
            dragFrom: null,
            dragOver: null,
            importOpen: false,
            importText: "",
            previewValues: null,
            previewMobile: false,
            columnTypes: COLUMN_TYPES,
            maxColumns: MAX_COLUMNS,
            maxFixedRows: MAX_FIXED_ROWS,
            maxSummary: MAX_SUMMARY_ROWS,
            maxDynamicRows: MAX_DYNAMIC_ROWS,
        };
    },
    computed: {
        dataRows() {
            return this.s.rows.mode === "fixed"
                ? this.s.rows.items
                : [this.s.rows.template];
        },
        isFormulaText() {
            return !!this.editing && String(this.editText).trim().startsWith("=");
        },
        // Settings with the formula being typed applied, so results show live.
        liveSettings() {
            if (!this.editing || !this.isFormulaText) return this.s;
            const copy = JSON.parse(JSON.stringify(this.s));
            setCellInput(copy, this.editing, this.editText);
            return copy;
        },
        results() {
            const settings = normalizeTableSettings(this.liveSettings);
            return computeSafe(settings, sampleInputValues(settings));
        },
        problems() {
            return validateTable(this.s, (key, args) => this.t(key, args));
        },
        problemsByWhere() {
            const out = {};
            this.problems.forEach((p) => {
                String(p.where || "").split(",").forEach((where) => {
                    out[where] = out[where] ? out[where] + "\n" + p.message : p.message;
                });
            });
            return out;
        },
        highlighted() {
            let formula = null;
            if (this.isFormulaText) formula = String(this.editText).trim().slice(1);
            else if (this.selected) {
                const cfg = cellCfgOf(this.s, this.selected);
                if (hasFormula(cfg)) formula = cfg.formula;
            }
            if (!formula) return {};
            const out = {};
            referencedRefs(this.s, formula).forEach((ref) => {
                out[ref] = true;
            });
            return out;
        },
        selectedRef() {
            return this.selected ? cellRefOf(this.s, this.selected) : "";
        },
        selectedText() {
            if (!this.selected) return "";
            if (sameTarget(this.selected, this.editing)) return this.editText;
            return cellInputText(cellCfgOf(this.s, this.selected), this.s.columns[this.selected.col]);
        },
        selectedIsFormula() {
            return !!this.selected && hasFormula(cellCfgOf(this.s, this.selected));
        },
        selectedLocked() {
            const cfg = this.selected && cellCfgOf(this.s, this.selected);
            return !!(cfg && cfg.locked);
        },
        selectedResult() {
            if (!this.selected) return null;
            const isFormula = sameTarget(this.selected, this.editing) ? this.isFormulaText : this.selectedIsFormula;
            if (!isFormula) return null;
            return this.resultText(this.selected);
        },
        menuColumn() {
            return this.menu ? this.s.columns[this.menu.col] : null;
        },
        valueNumberChoices() {
            const choices = valueNumberOptions(this.s).map((opt) => ({
                value: opt.value,
                label: opt.ref + " · " + (opt.row || (opt.summary ? this.t("forms-table-summary_row") : this.t("forms-table-row"))) + " · " + (opt.column || ""),
            }));
            const current = this.s.valueNumber;
            if (current && !choices.some((c) => c.value === current)) {
                choices.unshift({ value: current, label: this.t("forms-table-value_number_invalid", [current]) });
            }
            return choices;
        },
        valueNumberModel: {
            get() {
                return this.s.valueNumber || "";
            },
            set(value) {
                if (value) this.$set(this.s, "valueNumber", value);
                else this.$delete(this.s, "valueNumber");
            },
        },
        previewSettings() {
            return normalizeTableSettings(this.s);
        },
    },
    watch: {
        value(value) {
            if (value && value !== this.s) {
                this.s = prepareTableSettings(value, this.defaultLabels());
            }
        },
        s: {
            deep: true,
            immediate: true,
            handler() {
                this.$emit("input", this.s);
            },
        },
    },
    mounted() {
        document.addEventListener("mousedown", this.onDocumentMouseDown);
        document.addEventListener("keydown", this.onDocumentKeydown);
    },
    beforeDestroy() {
        document.removeEventListener("mousedown", this.onDocumentMouseDown);
        document.removeEventListener("keydown", this.onDocumentKeydown);
    },
    methods: {
        t(term, replaces) {
            return this.$root.t(term, replaces);
        },
        defaultLabels() {
            return {
                item: this.$root.t("forms-table-default_item"),
                quantity: this.$root.t("forms-table-default_quantity"),
                addLabel: "",
                column: this.$root.t("forms-table-column"),
            };
        },
        letter(j) {
            return columnLetter(j);
        },
        typeShort(type) {
            return { text: "Aa", number: "#", select: "▾", date: "31" }[type] || "";
        },
        defaultWidth(col) {
            return defaultMinWidth(col);
        },
        editorWidth(col) {
            return Math.min(Number(col.minWidth) || defaultMinWidth(col), 240);
        },
        isEditing(target) {
            return sameTarget(this.editing, target);
        },
        isLocked(target) {
            const cfg = cellCfgOf(this.s, target);
            return !!(cfg && cfg.locked && !hasFormula(cfg));
        },
        errorKey(target) {
            return target.kind === "summary"
                ? "s:" + target.row + ":" + target.col
                : target.row + ":" + target.col;
        },
        resultText(target) {
            const cfg = cellCfgOf(this.liveSettings, target);
            if (hasFormula(cfg) && cfg.formula.indexOf("#REF") !== -1) return "#REF";
            const code = (this.results.errors || {})[this.errorKey(target)];
            if (code) return "#" + code.toUpperCase();
            const v = target.kind === "summary"
                ? ((this.results.summary || [])[target.row] || [])[target.col]
                : ((this.results.values || [])[target.row] || [])[target.col];
            const text = formatTableCell(v, this.s.columns[target.col]);
            return text === "" ? "0" : text;
        },
        cellDisplay(target) {
            const cfg = cellCfgOf(this.s, target);
            if (hasFormula(cfg)) return this.resultText(target);
            if (!cfg || cfg.value === undefined || cfg.value === null) return "";
            return formatTableCell(cfg.value, this.s.columns[target.col]);
        },
        cellClasses(target) {
            const cfg = cellCfgOf(this.s, target);
            const ref = cellRefOf(this.s, target);
            return {
                "tqs-cell": true,
                "is-selected": sameTarget(this.selected, target),
                "is-editing": this.isEditing(target),
                "is-formula": hasFormula(cfg),
                "is-locked": !!(cfg && cfg.locked),
                "is-default": !!(cfg && !hasFormula(cfg) && cfg.value !== undefined && cfg.value !== null),
                "is-ref": !!this.highlighted[ref],
                "is-number": this.s.columns[target.col].type === "number",
                "has-error": !!this.problemsByWhere[ref] || !!(this.results.errors || {})[this.errorKey(target)],
            };
        },
        cellTitle(target) {
            const ref = cellRefOf(this.s, target);
            const cfg = cellCfgOf(this.s, target);
            const parts = [];
            if (this.problemsByWhere[ref]) parts.push(this.problemsByWhere[ref]);
            const code = (this.results.errors || {})[this.errorKey(target)];
            if (code) parts.push(this.t("forms-table-formula_error", [code]));
            if (hasFormula(cfg)) parts.push("=" + cfg.formula);
            return parts.join("\n") || null;
        },

        /* ---- Cell editing ---- */
        onCellMouseDown(event, target) {
            if (this.editing && this.isFormulaText && !sameTarget(target, this.editing)) {
                // Keep the formula input focused and insert the clicked cell.
                event.preventDefault();
                this.insertRef(cellRefOf(this.s, target));
                this.skipNextClick = true;
            }
        },
        onLetterMouseDown(event, j) {
            if (this.editing && this.isFormulaText) {
                event.preventDefault();
                this.insertRef(columnLetter(j) + ":" + columnLetter(j));
            }
        },
        onCellClick(target) {
            if (this.skipNextClick) {
                this.skipNextClick = false;
                return;
            }
            if (this.isEditing(target)) return;
            this.startEdit(target);
        },
        startEdit(target) {
            this.closeMenu();
            this.selected = target;
            this.editing = target;
            this.inputError = "";
            this.lastRefRange = null;
            this.editText = cellInputText(cellCfgOf(this.s, target), this.s.columns[target.col]);
            this.$nextTick(() => {
                const input = this.cellInputEl();
                if (input) {
                    input.focus();
                    input.setSelectionRange(input.value.length, input.value.length);
                }
            });
        },
        cellInputEl() {
            const refs = this.$refs.cellInput;
            return Array.isArray(refs) ? refs[0] : refs;
        },
        insertRef(ref) {
            const input = this.cellInputEl();
            const text = String(this.editText);
            let start = input ? input.selectionStart : text.length;
            let end = input ? input.selectionEnd : text.length;
            // A second click replaces the reference just inserted.
            if (this.lastRefRange && this.lastRefRange.end === start && start === end) {
                start = this.lastRefRange.start;
            }
            this.editText = text.slice(0, start) + ref + text.slice(end);
            const caret = start + ref.length;
            this.lastRefRange = { start, end: caret };
            this.$nextTick(() => {
                const el = this.cellInputEl();
                if (el) {
                    el.focus();
                    el.setSelectionRange(caret, caret);
                }
            });
        },
        commitEdit() {
            if (!this.editing) return true;
            const errorKey = setCellInput(this.s, this.editing, this.editText);
            if (errorKey) {
                this.inputError = this.t("forms-table-" + errorKey);
                return false;
            }
            this.inputError = "";
            return true;
        },
        stopEditing() {
            if (this.editing) this.commitEdit();
            this.editing = null;
            this.lastRefRange = null;
        },
        onEditBlur() {
            if (!this.editing) return;
            this.commitEdit();
            this.editing = null;
            this.lastRefRange = null;
        },
        onEditKeydown(event) {
            if (event.key === "Escape") {
                event.preventDefault();
                this.inputError = "";
                this.editing = null;
                return;
            }
            if (event.key === "Enter" || event.key === "Tab") {
                event.preventDefault();
                const current = this.editing;
                if (!this.commitEdit()) return;
                this.editing = null;
                const next = this.nextTarget(current, event.key === "Tab" ? (event.shiftKey ? "left" : "right") : "down");
                if (next) this.startEdit(next);
                return;
            }
            if (event.key !== "Shift") this.lastRefRange = null;
        },
        nextTarget(target, direction) {
            const n = this.s.columns.length;
            if (direction === "right" && target.col < n - 1) return Object.assign({}, target, { col: target.col + 1 });
            if (direction === "left" && target.col > 0) return Object.assign({}, target, { col: target.col - 1 });
            if (direction === "down") {
                if (target.kind === "data" && target.row < this.dataRows.length - 1) {
                    return Object.assign({}, target, { row: target.row + 1 });
                }
                if (target.kind === "summary" && target.row < this.s.rows.summary.length - 1) {
                    return Object.assign({}, target, { row: target.row + 1 });
                }
            }
            return null;
        },
        toggleLock() {
            if (!this.selected || this.selected.kind !== "data") return;
            if (this.editing) this.commitEdit();
            toggleCellLock(this.s, this.selected);
        },

        /* ---- Structure ---- */
        structural(change) {
            this.stopEditing();
            this.selected = null;
            this.closeMenu();
            change();
        },
        addColumn() {
            this.structural(() => {
                const at = this.s.columns.length;
                insertColumn(this.s, at, newColumn("", "text"));
                this.$nextTick(() => {
                    const inputs = this.$el.querySelectorAll(".tqs-header-input");
                    if (inputs[at]) inputs[at].focus();
                });
            });
        },
        removeCol(j) {
            this.structural(() => removeColumn(this.s, j));
        },
        moveCol(from, to) {
            this.structural(() => moveColumn(this.s, from, to));
        },
        duplicateCol(j) {
            this.structural(() => duplicateColumn(this.s, j));
        },
        addRow() {
            this.structural(() => addFixedRow(this.s, ""));
        },
        removeRow(i) {
            this.structural(() => removeFixedRow(this.s, i));
        },
        moveRow(from, to) {
            this.structural(() => moveFixedRow(this.s, from, to));
        },
        addSummary() {
            this.structural(() => addSummaryRow(this.s, this.t("forms-table-summary_default_label")));
        },
        removeSummary(k) {
            this.structural(() => removeSummaryRow(this.s, k));
        },
        changeMode(mode) {
            if (this.s.rows.mode === mode) return;
            this.structural(() => setRowsMode(this.s, mode));
        },
        changeType(type) {
            const col = this.menuColumn;
            this.$set(col, "type", type);
            if (type === "select" && !(col.options && col.options.length)) {
                this.$set(col, "options", [{ text: "", order: 1, removed: false, new: true }]);
            }
        },
        addOption(afterIndex) {
            const options = this.menuColumn.options;
            const at = Number.isInteger(afterIndex) ? afterIndex + 1 : options.length;
            options.splice(at, 0, { text: "", order: at + 1, removed: false, new: true });
            this.$nextTick(() => {
                const inputs = this.$refs.menu && this.$refs.menu.querySelectorAll(".tqs-option input");
                if (inputs && inputs[at]) inputs[at].focus();
            });
        },
        removeOption(index) {
            const options = this.menuColumn.options;
            if (options[index].new) options.splice(index, 1);
            else this.$set(options[index], "removed", true);
        },

        /* ---- Column drag ---- */
        onDragStart(event, j) {
            this.dragFrom = j;
            this.stopEditing();
            if (event.dataTransfer) {
                event.dataTransfer.effectAllowed = "move";
                event.dataTransfer.setData("text/plain", String(j));
            }
        },
        onDrop(j) {
            const from = this.dragFrom;
            this.dragOver = null;
            this.dragFrom = null;
            if (from === null || from === j) return;
            this.moveCol(from, j);
        },

        /* ---- Column menu ---- */
        openMenu(event, j) {
            this.stopEditing();
            if (this.menu && this.menu.col === j) {
                this.menu = null;
                return;
            }
            const root = this.$refs.root.getBoundingClientRect();
            const th = event.currentTarget.closest("th").getBoundingClientRect();
            const width = 320;
            const left = Math.max(0, Math.min(th.left - root.left, root.width - width));
            this.menu = { col: j, top: th.bottom - root.top + 4, left };
        },
        closeMenu() {
            this.menu = null;
        },
        onDocumentMouseDown(event) {
            if (this.menu && this.$refs.menu && !this.$refs.menu.contains(event.target)) {
                this.menu = null;
            }
        },
        onDocumentKeydown(event) {
            if (event.key === "Escape" && this.menu) this.menu = null;
        },

        /* ---- Paste ---- */
        clipboardText(event) {
            const data = event.clipboardData || window.clipboardData;
            return data ? data.getData("text") || data.getData("Text") || "" : "";
        },
        onCellPaste(event) {
            const text = this.clipboardText(event);
            if (!looksLikeTable(text)) return;
            event.preventDefault();
            const anchor = this.editing;
            this.editing = null;
            this.structural(() => this.reportSkipped(pasteMatrixAt(this.s, anchor, parseTsv(text), this.defaultLabels())));
        },
        onHeaderPaste(event, j) {
            const text = this.clipboardText(event);
            if (!looksLikeTable(text)) return;
            event.preventDefault();
            const matrix = parseTsv(text);
            this.structural(() => {
                matrix[0].forEach((label, dj) => {
                    const col = j + dj;
                    if (col >= MAX_COLUMNS) return;
                    if (col >= this.s.columns.length) insertColumn(this.s, col, newColumn("", "text"));
                    this.s.columns[col].label = label;
                });
                if (matrix.length > 1) {
                    this.reportSkipped(pasteMatrixAt(this.s, { kind: "data", row: 0, col: j }, matrix.slice(1), this.defaultLabels()));
                }
            });
        },
        onRowLabelPaste(event, i) {
            const text = this.clipboardText(event);
            if (!looksLikeTable(text)) return;
            event.preventDefault();
            const matrix = parseTsv(text);
            this.structural(() => {
                matrix.forEach((line, di) => {
                    const r = i + di;
                    if (r >= MAX_FIXED_ROWS) return;
                    while (r >= this.s.rows.items.length) addFixedRow(this.s, "");
                    this.s.rows.items[r].label = line[0] || "";
                });
                const rest = matrix.map((line) => line.slice(1));
                if (rest.some((line) => line.length)) {
                    this.reportSkipped(pasteMatrixAt(this.s, { kind: "data", row: i, col: 0 }, rest, this.defaultLabels()));
                }
            });
        },
        runImport() {
            this.structural(() => {
                importMatrix(this.s, parseTsv(this.importText), this.defaultLabels());
            });
            this.importText = "";
            this.importOpen = false;
        },
        reportSkipped(skipped) {
            this.inputError = skipped ? this.t("forms-table-paste_skipped", [skipped]) : "";
        },

        openTest() {
            this.stopEditing();
            this.closeMenu();
            this.previewValues = null;
            this.view = "test";
        },
        /* Called by the question editor before saving. */
        flush() {
            this.stopEditing();
            this.closeMenu();
        },
    },
};
</script>

<style scoped lang="less">
.table-question-settings {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 10px;
    min-width: 0;
}

.tqs-toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
}

.tqs-toolbar-right {
    display: flex;
    align-items: center;
    gap: 8px;
}

.tqs-segmented {
    display: inline-flex;
    padding: 2px;
    border-radius: 8px;
    background: #f3f4f6;

    button {
        border: 0;
        background: transparent;
        padding: 5px 12px;
        border-radius: 6px;
        font-size: 13px;
        color: #4d5761;

        &.active {
            background: #fff;
            color: #111927;
            box-shadow: 0 1px 2px rgba(16, 24, 40, 0.08);
        }
    }
}

.tqs-hint {
    margin: 0;
    font-size: 12px;
    color: #6c737f;
}

.tqs-import {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 10px;
    border: 1px dashed #d2d6db;
    border-radius: 10px;
    background: #fff;
}

.tqs-import-text {
    width: 100%;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 8px;
    font-family: monospace;
    font-size: 12px;
}

.tqs-import-actions {
    display: flex;
    align-items: center;
    gap: 8px;

    .tqs-hint {
        flex: 1;
    }
}

.tqs-formula-bar {
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: 34px;
    padding: 4px 8px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    background: #fff;
    font-size: 13px;
}

.tqs-ref {
    min-width: 34px;
    font-weight: 600;
    color: #111927;
}

.tqs-fx {
    color: #9da4ae;
    font-style: italic;
}

.tqs-formula-text {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-family: monospace;

    &.muted {
        font-family: inherit;
        color: #9da4ae;
    }
}

.tqs-formula-result {
    color: #0b6b3a;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
}

.tqs-lock {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    background: #fff;
    padding: 2px 8px;
    font-size: 12px;
    color: #4d5761;

    &.active {
        background: #fff7d4;
        border-color: #fedf89;
        color: #b54708;
    }

    &:disabled {
        opacity: 0.5;
    }
}

.tqs-cell-error {
    margin: 0;
    font-size: 12px;
    color: #b91c1c;
}

.tqs-grid-wrap {
    overflow-x: auto;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    background: #fff;
}

.tqs-grid {
    border-collapse: separate;
    border-spacing: 0;
    font-size: 13px;

    th,
    td {
        border-right: 1px solid #f0f1f3;
        border-bottom: 1px solid #f0f1f3;
        height: 34px;
        padding: 0;
        vertical-align: middle;
    }

    .tqs-letters th {
        height: 26px;
        background: #f9fafb;
        color: #6c737f;
        font-size: 11px;
        font-weight: 600;
        text-align: center;
        position: relative;
        cursor: grab;
        user-select: none;

        &.is-drop {
            box-shadow: inset 3px 0 0 #2aaa4c;
        }

        &.has-error {
            color: #b91c1c;
        }
    }

    .tqs-letter {
        margin-right: 4px;
    }

    .tqs-type-tag {
        color: #9da4ae;
        font-weight: 400;
        font-size: 10px;
    }

    .tqs-col-menu-btn {
        position: absolute;
        right: 2px;
        top: 2px;
        border: 0;
        background: transparent;
        padding: 0 6px;
        line-height: 20px;
        border-radius: 4px;
        color: #4d5761;
        font-size: 14px;

        &:hover {
            background: #e5e7eb;
        }
    }

    .tqs-headers th {
        background: #f9fafb;
        font-weight: 500;
        position: relative;
    }

    .tqs-header-input,
    .tqs-row-label-input {
        width: 100%;
        height: 34px;
        border: 0;
        background: transparent;
        padding: 6px 8px;
        font-weight: 500;
        color: #111927;
        font-size: 13px;

        &:focus {
            outline: 2px solid #2aaa4c;
            outline-offset: -2px;
            background: #fff;
        }
    }

    .tqs-required {
        position: absolute;
        right: 4px;
        top: 6px;
        color: #d92d20;
    }

    .tqs-rownum {
        width: 44px;
        min-width: 44px;
        background: #f9fafb;
        color: #6c737f;
        font-size: 11px;
        font-weight: 600;
        text-align: center;
        position: relative;

        &:hover .tqs-row-actions {
            display: flex;
        }
    }

    .tqs-row-actions {
        display: none;
        position: absolute;
        left: 100%;
        top: 50%;
        transform: translateY(-50%);
        z-index: 3;
        gap: 2px;
        padding: 2px;
        border: 1px solid #e5e7eb;
        border-radius: 6px;
        background: #fff;
        box-shadow: 0 4px 8px rgba(16, 24, 40, 0.08);

        button {
            border: 0;
            background: transparent;
            font-size: 10px;
            padding: 2px 5px;
            color: #4d5761;

            &:disabled {
                opacity: 0.3;
            }
        }
    }

    .tqs-label-col {
        min-width: 150px;
        background: #f9fafb;
        text-align: left;
    }

    .tqs-muted {
        padding: 0 8px;
        color: #6c737f;
        font-size: 12px;
        font-weight: 400;
    }

    .tqs-each-row {
        display: block;
        padding: 0 8px;
        color: #6c737f;
        font-size: 12px;
        font-weight: 400;
        font-style: italic;
    }

    .tqs-add-col {
        width: 36px;
        min-width: 36px;

        button {
            border: 0;
            background: transparent;
            color: #2aaa4c;
            padding: 2px 8px;

            &:disabled {
                opacity: 0.4;
            }
        }
    }

    .tqs-cell {
        position: relative;
        cursor: cell;
        max-width: 240px;

        &.is-number {
            text-align: right;
        }

        &.is-default .tqs-cell-text {
            color: #6c737f;
        }

        &.is-locked {
            background: #fffcf0;

            .tqs-cell-text {
                color: #384250;
            }
        }

        &.is-formula {
            background: #f3faf4;

            .tqs-cell-text {
                color: #0b6b3a;
            }
        }

        &.is-ref {
            box-shadow: inset 0 0 0 2px #7cd4fd;
            background: #f0f9ff;
        }

        &.has-error {
            background: #fef3f2;

            .tqs-cell-text {
                color: #b91c1c;
            }
        }

        &.is-selected {
            box-shadow: inset 0 0 0 2px #2aaa4c;
        }

        &.is-editing {
            z-index: 2;
        }
    }

    .tqs-cell-text {
        display: block;
        padding: 6px 8px;
        min-height: 34px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        font-variant-numeric: tabular-nums;
    }

    .tqs-cell-lock {
        position: absolute;
        top: 3px;
        left: 3px;
        color: #b54708;
    }

    .tqs-cell-input {
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        height: 100%;
        border: 0;
        outline: 2px solid #2aaa4c;
        outline-offset: -2px;
        padding: 6px 8px;
        background: #fff;
        font-size: 13px;

        &.is-formula-input {
            width: auto;
            min-width: 100%;
            width: 280px;
            font-family: monospace;
            box-shadow: 0 4px 12px rgba(16, 24, 40, 0.12);
        }
    }

    .tqs-add-row td {
        padding: 4px 8px;

        button {
            display: inline-flex;
            align-items: center;
            gap: 4px;
            border: 0;
            background: transparent;
            color: #2aaa4c;
            font-size: 12px;

            &:disabled {
                opacity: 0.4;
            }
        }
    }

    .tqs-summary th,
    .tqs-summary td {
        border-top: 1px solid #d2d6db;
    }
}

.tqs-below {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
}

.tqs-fields {
    display: grid;
    grid-template-columns: 120px 120px 1fr;
    gap: 10px;

    @media screen and (max-width: 767px) {
        grid-template-columns: 1fr 1fr;
    }
}

.tqs-value-number {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.tqs-field-label {
    font-size: 12px;
    color: #4d5761;
}

.tqs-select,
.tqs-text {
    width: 100%;
    height: 34px;
    border: 1px solid #d2d6db;
    border-radius: 8px;
    padding: 4px 8px;
    background: #fff;
    font-size: 13px;
}

.tqs-test-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
}

.tqs-problems {
    margin: 0;
    padding: 8px 12px 8px 28px;
    border-radius: 8px;
    background: #fef3f2;
    color: #b42318;
    font-size: 12px;
}

.tqs-menu {
    position: absolute;
    z-index: 20;
    width: 320px;
    max-height: 420px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 12px;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    background: #fff;
    box-shadow: 0 12px 24px rgba(16, 24, 40, 0.14);
}

.tqs-menu-title {
    font-weight: 600;
    font-size: 13px;
    color: #111927;
}

.tqs-menu-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;

    label {
        display: flex;
        flex-direction: column;
        gap: 2px;
        margin: 0;
    }
}

.tqs-check {
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 0;
    font-size: 13px;
}

.tqs-options {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.tqs-option {
    display: flex;
    align-items: center;
    gap: 6px;

    &.is-removed {
        opacity: 0.6;
    }
}

.tqs-removed-text {
    flex: 1;
    font-size: 12px;
    color: #b91c1c;
}

.tqs-icon-btn {
    border: 0;
    background: transparent;
    color: #6c737f;
    padding: 4px;
}

.tqs-link {
    align-self: flex-start;
    border: 0;
    background: transparent;
    padding: 0;
    font-size: 12px;
    color: #2aaa4c;
}

.tqs-menu-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    padding-top: 8px;
    border-top: 1px solid #f3f4f6;

    button {
        border: 1px solid #e5e7eb;
        border-radius: 6px;
        background: #fff;
        padding: 4px 8px;
        font-size: 12px;
        color: #384250;

        &.danger {
            color: #b42318;
        }

        &:disabled {
            opacity: 0.4;
        }
    }
}
</style>
